import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { TAnnonce } from '../../models/annonce/annonce';
import { getImagePath } from '../../utils/format/valueFormat';

const { Title, Paragraph } = Typography;

// Convert hex to RGB
const hexToRgb = (hex: string): [number, number, number] | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : null;
};

// Calculate relative luminance of a color (0-1)
const getLuminance = (r: number, g: number, b: number): number => {
    const [rs, gs, bs] = [r, g, b].map((val) => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Determine if color is bright or dark, returns appropriate text color
const getTextColor = (backgroundColor: string): string => {
    const rgb = hexToRgb(backgroundColor);
    if (!rgb) return '#ffffff'; // Default to white if can't parse
    
    const [r, g, b] = rgb;
    const luminance = getLuminance(r, g, b);
    
    return luminance > 0.5 ? '#0f172a' : '#ffffff';
};

// Generate cool gradient colors from base color with smooth fade
const generateCoolGradient = (baseColor: string): string => {
    const rgb = hexToRgb(baseColor);
    if (!rgb) {
        // Default cool gradient with smooth fade
        return 'linear-gradient(135deg, rgba(41,146,191,0.95) 0%, rgba(91,222,186,0.92) 35%, rgba(139,190,30,0.90) 70%, rgba(41,146,191,0.88) 100%)';
    }

    const [r, g, b] = rgb;
    const color1 = `rgba(${r}, ${g}, ${b}, 0.95)`;
    const color2 = `rgba(${Math.min(255, r + 40)}, ${Math.min(255, g + 35)}, ${Math.min(255, b + 60)}, 0.92)`;
    const color3 = `rgba(${Math.min(255, r + 25)}, ${Math.min(255, g + 55)}, ${Math.min(255, b + 25)}, 0.90)`;
    const color4 = `rgba(${r}, ${g}, ${b}, 0.88)`;
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 30%, ${color3} 60%, ${color4} 100%)`;
};

// Generate radial glow from base color with smoother fade
const generateRadialGlow = (baseColor: string): string => {
    const rgb = hexToRgb(baseColor);
    if (!rgb) return `radial-gradient(circle at 20% 30%, rgba(41,146,191,0.25), transparent 70%)`;
    
    const [r, g, b] = rgb;
    return `radial-gradient(circle at 20% 30%, rgba(${r},${g},${b},0.3) 0%, rgba(${r},${g},${b},0.15) 40%, transparent 70%), radial-gradient(circle at 80% 70%, rgba(${r + 30},${g + 45},${b + 30},0.25) 0%, rgba(${r + 30},${g + 45},${b + 30},0.1) 35%, transparent 60%)`;
};

const AnnonceBanner: React.FC<TAnnonce> = ({
    title,
    description,
    image_url,
    background_color = '#2992BF',
}) => {
    const coolGradient = useMemo(() => generateCoolGradient(background_color), [background_color]);
    const radialGlow = useMemo(() => generateRadialGlow(background_color), [background_color]);
    const textColor = useMemo(() => getTextColor(background_color), [background_color]);

    const bannerStyle = useMemo(
        () => ({
            position: 'relative' as const,
            overflow: 'hidden' as const,
            borderRadius: 28,
            marginBottom: 0,
            background: coolGradient,
            border: '2px solid rgba(255,255,255,0.4)',
            boxShadow: '0 25px 60px rgba(15,23,42,0.2), 0 0 0 1px rgba(255,255,255,0.3), inset 0 1px 0 rgba(255,255,255,0.5)',
            backdropFilter: 'blur(12px)',
            width: '100%',
        }),
        [coolGradient]
    );

    const overlayStyle = useMemo(
        () => ({
            position: 'absolute' as const,
            inset: 0,
            background: radialGlow,
            pointerEvents: 'none' as const,
            zIndex: 0,
        }),
        [radialGlow]
    );

    const patternOverlay = useMemo(
        () => ({
            position: 'absolute' as const,
            inset: 0,
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            pointerEvents: 'none' as const,
            zIndex: 1,
            opacity: 0.4,
        }),
        []
    );

    return (
        <div style={bannerStyle}>
            <div style={overlayStyle} />
            <div style={patternOverlay} />
            <div
                style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(12px, 2vw, 20px)',
                    padding: 'clamp(16px, 2.5vw, 24px)',
                    flexWrap: 'nowrap',
                }}
            >
                {image_url && (
                    <div
                        style={{
                            flexShrink: 0,
                            width: 'clamp(100px, 14vw, 180px)',
                            height: 'clamp(100px, 14vw, 180px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            padding: '8px 12px',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                inset: '-8px',
                                borderRadius: '50%',
                                background: `radial-gradient(circle, rgba(255,255,255,0.2), transparent 70%)`,
                                filter: 'blur(12px)',
                                zIndex: 0,
                            }}
                        />
                        <img
                            src={getImagePath(image_url)}
                            alt={title}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                filter: 'drop-shadow(0 4px 12px rgba(15,23,42,0.3))',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />
                    </div>
                )}
                <div
                    style={{
                        flex: 1,
                        minWidth: 0,
                        color: textColor,
                    }}
                >
                    <Title
                        level={3}
                        style={{
                            margin: 0,
                            marginBottom: 12,
                            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                            fontWeight: 800,
                            color: textColor,
                            lineHeight: 1.15,
                            textShadow: textColor === '#ffffff' 
                                ? '0 2px 12px rgba(15,23,42,0.3), 0 0 2px rgba(255,255,255,0.5)'
                                : '0 2px 12px rgba(255,255,255,0.5), 0 0 2px rgba(15,23,42,0.2)',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {title}
                    </Title>
                    {description && (
                        <Paragraph
                            style={{
                                margin: 0,
                                fontSize: 'clamp(1rem, 1.8vw, 1.15rem)',
                                color: textColor === '#ffffff' 
                                    ? 'rgba(255,255,255,0.98)' 
                                    : 'rgba(15,23,42,0.95)',
                                lineHeight: 1.7,
                                textShadow: textColor === '#ffffff'
                                    ? '0 1px 6px rgba(15,23,42,0.25)'
                                    : '0 1px 6px rgba(255,255,255,0.4)',
                                fontWeight: 500,
                            }}
                        >
                            {description.split('\n').map((line, index) => (
                                <span key={index}>
                                    {line}
                                    {index < description.split('\n').length - 1 && <br />}
                                </span>
                            ))}
                        </Paragraph>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnonceBanner;
